import React, { useEffect, useMemo, useCallback } from 'react'
import { Col, Container, Row, Stack } from 'react-bootstrap'
import { useSearchParams } from 'react-router-dom'
import { Button } from '../../../../components/Button/Button'
import { ScopedBlock } from '../../../../components/ScopedBlock/ScopedBlock'
import { MissingScopeAction } from '../../../../components/ScopedBlock/models'
import { useAppSelector } from '../../../../store/hooks'
import { selectAccountId } from '../../../../store/auth/authSlice'
import { useDeleteUserIntegrationMutation, useGetMeQuery } from '../../../../store/user/user'
import { Link } from 'react-router-dom'
import { createState, IntegrationServiceId } from '../../../../common/integrations'
import { getIntegrationRedirectUri } from '../../../Integration/utils'
import { IntegrationIcon } from '../../../../components/IntegrationIcon'

export type OutlookCalendarIntegrationRowProps = {
  onClick: (integration: IntegrationServiceId, defaultAuth: () => void) => void
  triggerAuth?: boolean
}

const ADMIN_CONSENT_LINK = import.meta.env.REACT_APP_MICROSOFT_ADMIN_CONSENT_LINK!

export const OutlookCalendarIntegrationRow: React.FC<OutlookCalendarIntegrationRowProps> = ({ onClick, triggerAuth }) => {
  const accountId = useAppSelector(selectAccountId)
  const { data: userData } = useGetMeQuery({ accountId: accountId }, { refetchOnMountOrArgChange: true })
  const [deleteUserIntegration, { isLoading: isDeletingIntegration }] = useDeleteUserIntegrationMutation()
  const hasOutlookCalendarIntegration = useMemo(() => {
    return !!(userData && userData.integrations && userData.integrations.some((i) => i.serviceId === IntegrationServiceId.OUTLOOK_CALENDAR))
  }, [userData])
  const [searchParams] = useSearchParams()

  const authorizeOutlookCalendar = useCallback(() => {
    const redirectUri = getIntegrationRedirectUri(IntegrationServiceId.OUTLOOK_CALENDAR)
    const state = createState()
    window.location.href = `${import.meta.env.REACT_APP_IAM_API_BASE_URL}/accounts/${accountId}/user-integrations/${IntegrationServiceId.OUTLOOK_CALENDAR}/enable?redirect_uri=${redirectUri}&state=${state}`
  }, [accountId])

  const uninstallOutlookCalendar = () => {
    const user = userData
    const integration = user!.integrations!.find((i) => i.serviceId === IntegrationServiceId.OUTLOOK_CALENDAR)
    deleteUserIntegration({
      accountId,
      payload: {
        userId: user!.id,
        integrationId: integration!.id,
      },
    })
  }

  useEffect(() => {
    if (!triggerAuth || searchParams.get('connect') !== 'true') return
    authorizeOutlookCalendar()
  }, [triggerAuth, searchParams, authorizeOutlookCalendar])

  return (
    <Container className='p-3 p-md-0 py-md-3 integration-container'>
      <Row className='d-flex justify-content-between'>
        <Col>
          <Stack direction='horizontal' className='gap-4 pb-4 pb-md-0'>
            <div className='rounded py-1 px-1 zocks-bg-color-gray-100'>
              <IntegrationIcon serviceId={IntegrationServiceId.OUTLOOK_CALENDAR} width={48} height={48} />
            </div>
            <div>
              <h6>Outlook Calendar</h6>
              <div>
                Sync Outlook Calendar events so that Zocks joins your calls automatically. <br />
                It might be needed for your admin to approve the integration. For more info, click{' '}
                <Link className='zocks-text-gray-900 text-decoration-underline' to={ADMIN_CONSENT_LINK}>
                  here
                </Link>{' '}
                .
              </div>
            </div>
          </Stack>
        </Col>
        <Col md='auto'>
          <Stack direction='horizontal' className='gap-3 align-items-start'>
            <div className='connect-button-container'>
              {hasOutlookCalendarIntegration ? (
                <div className='connect-button-container'>
                  <Button variant='primary' onClick={uninstallOutlookCalendar} loading={isDeletingIntegration}>
                    Disconnect
                  </Button>
                  <div className='pt-2 d-flex justify-content-end zocks-text-success'>
                    <i className='bi bi-check' />
                    Connected
                  </div>
                </div>
              ) : (
                <ScopedBlock
                  scope={{
                    scope: 'integration:connect',
                    superScope: ['integration:admin'],
                  }}
                  missingScopeAction={MissingScopeAction.DISABLE}
                >
                  <Button type='button' variant='primary' onClick={() => onClick(IntegrationServiceId.OUTLOOK_CALENDAR, authorizeOutlookCalendar)}>
                    Connect
                  </Button>
                </ScopedBlock>
              )}
            </div>
          </Stack>
        </Col>
      </Row>
    </Container>
  )
}
